import { Loader2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type {
  GetSubmissionAttemptDetailResponse,
} from '@/services/api/agentflowService';

const STATUS_DOT: Record<string, string> = {
  ready: 'bg-green-500',
  blocked: 'bg-amber-500',
  needs_rebuild: 'bg-amber-500',
  needs_re_evaluation: 'bg-amber-500',
  failed: 'bg-red-500',
  draft: 'bg-zinc-400',
  submitting: 'bg-blue-500',
  submitted: 'bg-green-500',
};

const STATUS_BADGE: Record<
  string,
  { label: string; className: string } | undefined
> = {
  ready: {
    label: 'READY',
    className: 'bg-green-50 text-green-700 border-green-200',
  },
  blocked: {
    label: 'BLOCKED',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  needs_rebuild: {
    label: 'BLOCKED',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  needs_re_evaluation: {
    label: 'BLOCKED',
    className: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  failed: {
    label: 'FAILED',
    className: 'bg-red-50 text-red-700 border-red-200',
  },
  submitting: {
    label: 'SUBMITTING',
    className: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  submitted: {
    label: 'SUBMITTED',
    className: 'bg-green-50 text-green-700 border-green-200',
  },
};

function getSubtitle(detail: GetSubmissionAttemptDetailResponse): string {
  const parts: string[] = [];
  const contactMethod = detail.contact_method;
  if (contactMethod?.recommended_contact_method?.provider_name) {
    parts.push(contactMethod.recommended_contact_method.provider_name);
  } else if (contactMethod?.manual_contact_method?.provider_name) {
    parts.push(contactMethod.manual_contact_method.provider_name);
  }
  return parts.join(' · ');
}

interface SubmissionAttemptHeaderProps {
  detail: GetSubmissionAttemptDetailResponse;
  submissionPhase?: 'idle' | 'submitting';
  onClose: () => void;
}

function getProgressLabel(
  submissionPhase: 'idle' | 'submitting',
  status: GetSubmissionAttemptDetailResponse['status'],
): string | null {
  if (submissionPhase === 'submitting' || status === 'submitting') {
    return 'Submitting package...';
  }
  return null;
}

export const SubmissionAttemptHeader = ({
  detail,
  submissionPhase = 'idle',
  onClose,
}: SubmissionAttemptHeaderProps) => {
  const badge = STATUS_BADGE[detail.status];
  const subtitle = getSubtitle(detail);
  const isSubmittingFlow = submissionPhase !== 'idle';
  const progressLabel = getProgressLabel(submissionPhase, detail.status);

  return (
    <div className="flex items-center justify-between gap-4 border-b border-zinc-200 px-6 py-3 shrink-0">
      <div className="flex items-center gap-3 min-w-0">
        <span
          className={cn(
            'inline-block h-2.5 w-2.5 rounded-full shrink-0',
            STATUS_DOT[detail.status] ?? 'bg-zinc-400',
          )}
        />
        <DialogTitle className="text-base font-semibold text-zinc-900 truncate">
          Submission Attempt
        </DialogTitle>
        {subtitle && (
          <span className="text-sm text-zinc-500 truncate">{subtitle}</span>
        )}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {progressLabel && (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {progressLabel}
          </span>
        )}
        {badge && (
          <span
            className={cn(
              'inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-semibold tracking-wider',
              badge.className,
            )}
          >
            {badge.label}
          </span>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onClose}
          disabled={isSubmittingFlow}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
